import { Meta } from "@solidjs/meta";
import { useQuery } from "@tanstack/solid-query";
import {
	type Component,
	For,
	lazy,
	Match,
	type ParentComponent,
	Show,
	Suspense,
	Switch
} from "solid-js";
import { Dynamic } from "solid-js/web";
import ElevenLabsIcon from "~/Icons/16_Icon_ElevenLabs.svg?component-solid";
import GeminiIcon from "~/Icons/16_Icon_gemini.svg?component-solid";
import GptIcon from "~/Icons/16_Icon_IconGPT.svg?component-solid";
import KlingIcon from "~/Icons/16_Icon_Kling.svg?component-solid";
import RunwayIcon from "~/Icons/16_Icon_runway.svg?component-solid";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import {
	createpremiumPopUpStore,
	PremiumPopUpContext
} from "~/solidJs/entities/PremiumPopUp/models/premiumPopUpContext";
import { CreativeTeamsSection } from "~/solidJs/entities/PremiumPopUp/ui/CreativeTeamsSection";
import { PremiumProductBlocV2 } from "~/solidJs/entities/PremiumPopUp/ui/PremiumProductBloc";
import { PricingFeaturesTableEntityV2 } from "~/solidJs/entities/PricingFeaturesTableEntity/PricingFeaturesTableEntityV2";
import { PricingFeedBackLogoSection } from "~/solidJs/entities/PricingFeedBackEntity/PricingFeedBackEntity";
import { PromoCodeFeatureV2 } from "~/solidJs/features/PromoCodeFeature/PromoCodeFeature";
import { isUserFree, SuspenseBlock, useCustomContext } from "~/solidJs/shared";
import { ProductsApi } from "~/solidJs/shared/api/ProductsApi";
import Icons from "~/solidJs/shared/ui/atoms/IconsComp";
import { SuspenseSkeleton } from "~/solidJs/shared/ui/atoms/Skeleton/Skeleton";
import ConfirmPlanChangeModal from "~/solidJs/widgets/ConfirmPlanChangeModal";
import { BackToWorkspaceLink } from "~/solidJs/widgets/UserPagesHeader/ui";
import { PricingPageContext, type PricingPageContextType, usePricingStore } from "../methods";
import { AllFeaturesTable } from "./AllFeaturesTable";
import { CommentAndReviewsSection } from "./CommentAndReviewsSection";
import { ProductsSectionV3 } from "./ProductsSection";
import { QaSection } from "./QaSection";
import { SelectPaymentCurrency } from "./SelectPaymentCurrency";
import { SwitchPaymentLength } from "./SwitchPaymentLength";
import { SwitchSubscriptionType } from "./SwitchSubscriptionType";
import { Testimonials } from "./Testimonials";
import { ViewAllFeature } from "./ViewAllFeature";

const Layout: ParentComponent<{ pricingStore: PricingPageContextType }> = (props) => {
	return (
		<>
			<Show when={props.pricingStore.getShouldShowLegacyHeader()}>
				<div class="typography-text-17-medium flex items-center justify-center bg-status-warning py-3 text-background-orange">
					<div>
						You're currently using Legacy plan, you can look at old Pricing{" "}
						<button
							type="button"
							class="typography-text-17-medium inline-block text-background-orange underline"
							onclick={props.pricingStore.toggleLegacyPlans}
						>
							here
						</button>{" "}
						or switch to a new plan below
					</div>
				</div>
			</Show>
			<SuspenseSkeleton variant="dark" class="h-16 w-full">
				{lazy(() => import("~/solidJs/widgets/UserPagesHeader"))({ class: "bg-white" })}
			</SuspenseSkeleton>
			<Suspense>
				{lazy(
					() =>
						import(
							"~/solidJs/widgets/TrainPanelAllModalsWidget/TrainPanelAllModalsWidget"
						)
				)({})}
			</Suspense>
			<main
				data-test="premium-pop-up"
				class="default-scrollbar h-full w-full flex-col justify-start overflow-auto bg-white"
			>
				{props.children}
			</main>
		</>
	);
};

const MobileSwitchers = () => {
	return (
		<div class="md:custom-tw-hidden sticky top-0 z-10 flex w-[calc(100%+1.50rem)] flex-col gap-6 rounded-b-2xl bg-white pt-5 pb-5 shadow-md">
			<div class="flex w-full items-center justify-between pr-2.5 pl-4">
				<SelectPaymentCurrency />
				<Show when={isUserFree(getUserStore())}>
					<div class="w-max">
						<PromoCodeFeatureV2 />
					</div>
				</Show>
			</div>
			<div class="relative flex h-10 w-full items-center justify-between gap-6 pr-1.5">
				<div class="flex-1" />
				<SwitchSubscriptionType />
				<div class="flex flex-1 justify-end">
					<SwitchPaymentLength />
				</div>
			</div>
		</div>
	);
};

const DesktopSwitchers = () => {
	return (
		<div class="custom-tw-hidden w-full flex-col gap-10 md:flex">
			<div class="relative flex w-full items-end justify-between">
				<SelectPaymentCurrency />
				<div class="-translate-x-1/2 absolute left-1/2">
					<SwitchSubscriptionType />
				</div>
				<SwitchPaymentLength />
			</div>
			<Show when={isUserFree(getUserStore())}>
				<PromoCodeFeatureV2 />
			</Show>
		</div>
	);
};

const NewPricing: Component = () => {
	const pricingStore = useCustomContext(PricingPageContext);
	const topIcon = [KlingIcon, RunwayIcon, GptIcon, GeminiIcon, ElevenLabsIcon, Icons.BFL.default];

	return (
		<div class="flex flex-col scroll-smooth bg-white">
			<div class="custom-tw-hidden p-5 md:block">
				<BackToWorkspaceLink />
			</div>
			<div class="flex flex-col gap-20 px-3 pb-24 2xl:px-24">
				<div class="flex w-full flex-col items-center gap-5 pt-3 md:gap-0 md:pt-0">
					<div class="flex flex-col items-center justify-center gap-3.5">
						<div class="flex p-1.5">
							<For each={topIcon}>
								{(icon) => (
									<div class="-ml-1 flex size-8 shrink-0 items-center justify-center rounded-full border border-border-secondary bg-white p-1.5">
										<Dynamic component={icon} class="shrink-0" />
									</div>
								)}
							</For>
						</div>
						<h1 class="header-48-semibold whitespace-pre-wrap text-center">
							All AI tools included{"\n"}in every plan
						</h1>
					</div>
					<MobileSwitchers />
					<div class="flex w-full flex-col gap-10">
						<DesktopSwitchers />
						<ProductsSectionV3
							forceSelectedPlan={pricingStore.getUserActivePlan() || undefined}
						/>
					</div>
				</div>
				<div class="flex w-full flex-col items-center justify-start gap-12 md:gap-44">
					<div class="custom-tw-hidden flex-col items-center justify-center gap-8 md:flex">
						<ViewAllFeature />
					</div>
					<Testimonials />
					<AllFeaturesTable />
					<QaSection />
					<CommentAndReviewsSection />
				</div>
			</div>
			<Suspense>
				{lazy(() => import("~/solidJs/widgets/UnsubscribeModalWidget"))({})}
			</Suspense>
			<ConfirmPlanChangeModal />
		</div>
	);
};

const OldPricing: Component = () => {
	const tariffs = useQuery(() => ({
		queryKey: ["premium-pop-up-legacy"],
		queryFn: () => ProductsApi.getLegacyProductList()
	}));

	const tariffsFeatures = useQuery(() => ({
		queryKey: ["premium-pop-up-features"],
		queryFn: () => ProductsApi.getPricingTableDataGrouped()
	}));

	const premiumPopUpStore = createpremiumPopUpStore(false);

	return (
		<PremiumPopUpContext.Provider value={premiumPopUpStore}>
			<article
				data-test={"premium-pop-up"}
				class={
					"relative flex h-full w-full flex-col items-center justify-start gap-32 overflow-y-auto overflow-x-visible bg-white px-14 py-8"
				}
				onclick={(e) => e.stopPropagation()}
			>
				<SuspenseBlock>
					<Show when={tariffs.data} keyed>
						{(items) => (
							<PremiumProductBlocV2 forceSelectedPlan="plus" itemsToRender={items} />
						)}
					</Show>

					<Show when={tariffsFeatures.data && tariffs.data} keyed>
						{(features) => (
							<PricingFeaturesTableEntityV2
								selectedPriceId={"plus"}
								tariffsResFeatures={tariffsFeatures.data}
								tariffsRes={features}
							/>
						)}
					</Show>

					<CreativeTeamsSection />
					<PricingFeedBackLogoSection />
				</SuspenseBlock>
			</article>
		</PremiumPopUpContext.Provider>
	);
};

interface PricingSwitchProps {
	pricingStore: PricingPageContextType;
}

const PricingSwitch: Component<PricingSwitchProps> = (props) => {
	return (
		<Switch>
			<Match when={props.pricingStore.getShouldShowLegacyPlans()}>
				<OldPricing />
			</Match>
			<Match when={!props.pricingStore.getShouldShowLegacyPlans()}>
				<NewPricing />
			</Match>
		</Switch>
	);
};

export const PricingPage: Component = () => {
	const pricingStore = usePricingStore();
	const bannerStore = createpremiumPopUpStore();

	return (
		<Layout pricingStore={pricingStore}>
			<Meta name="viewport" content="width=device-width, initial-scale=1" />
			<PricingPageContext.Provider value={pricingStore}>
				<PremiumPopUpContext.Provider value={bannerStore}>
					<PricingSwitch pricingStore={pricingStore} />
				</PremiumPopUpContext.Provider>
			</PricingPageContext.Provider>
		</Layout>
	);
};
